/**
 * LoadingState — skeleton grid for the Vault cards + a generic
 * "still working" spinner. The spinner IS the GI orbit mark: every
 * wait surface in the product now shows the spinning trademark,
 * reinforcing brand on the exact screens users linger on.
 */

import { GiOrbitLogo } from '@/components/GiOrbitLogo';
import { cn } from '@/utils/cn';

export function SkeletonCard({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        'animate-pulse rounded-lg border border-border bg-surface p-5',
        'space-y-3',
        className,
      )}
      aria-hidden="true"
    >
      <div className="flex items-center justify-between">
        <div className="h-4 w-20 rounded bg-surface-raised" />
        <div className="h-4 w-12 rounded bg-surface-raised" />
      </div>
      <div className="h-5 w-3/4 rounded bg-surface-raised" />
      <div className="h-3 w-full rounded bg-surface-raised" />
      <div className="h-3 w-2/3 rounded bg-surface-raised" />
      <div className="flex gap-2 pt-2">
        <div className="h-5 w-14 rounded bg-surface-raised" />
        <div className="h-5 w-14 rounded bg-surface-raised" />
      </div>
    </div>
  );
}

export function SkeletonGrid({ count = 4 }: { count?: number }) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {Array.from({ length: count }).map((_, i) => (
        <SkeletonCard key={i} />
      ))}
    </div>
  );
}

export function Spinner({
  className,
  size = 'xs',
}: {
  className?: string;
  /** xs for inline button spinners, md for page-level wait
   *  regions, lg for hero wait screens. */
  size?: 'xs' | 'md' | 'lg';
}) {
  // `aria-live` sits on a wrapper so the orbit's own role attrs
  // remain undisturbed, and screen readers still get a polite
  // "Loading" announcement the moment the spinner mounts.
  return (
    <span
      role="status"
      aria-live="polite"
      aria-label="Loading"
      className={cn('inline-flex items-center justify-center text-spark', className)}
    >
      <GiOrbitLogo size={size} decorative />
    </span>
  );
}
